// frontend/packages/business-console/src/components/build-interview/stepMeta.js
//
// Per-step presentation metadata for the redesigned build interview (Website
// Builder Redesign, Phase 1). The server (buildInterviewService) owns the step
// ORDER, titles, prompts, modes, and skippable flags; this file only adds the
// front-end-only trimmings the redesign calls for, keyed by the same step ids:
//   - icon: the journey-rail / question-card glyph (console MUI icon set; the
//     mock's line SVGs are stand-ins per the handoff).
//   - headline: a short, warm question shown as the big H1. Falls back to the
//     server prompt when absent, so no backend copy change is needed in P1.
//   - chips: tap-to-add example scaffolds for the freeform steps (inserted into
//     the answer box). Structured steps (brand/goals/proof/...) use their own
//     widgets and carry no chips.
//
// Copy rule: customer-visible strings here are em-dash-free.

import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

/** chip helper: label shown on the chip + the scaffold text inserted on tap. */
const chip = (label, insert) => ({ label, insert: insert ?? `${label}: ` });

export const STEP_META = {
  basics: {
    icon: StorefrontOutlinedIcon,
    headline: 'Tell us about your business',
    chips: [
      chip('Business name'),
      chip('What we do'),
      chip('Years in business'),
      chip('Licenses or certifications'),
    ],
  },
  services: {
    icon: HandymanOutlinedIcon,
    headline: 'What do you do for customers?',
    chips: [
      chip('Main service'),
      chip('Other services'),
      chip('Emergency or 24/7'),
      chip('Our #1 money maker'),
    ],
  },
  serviceArea: {
    icon: PlaceOutlinedIcon,
    headline: 'Where do you work?',
    chips: [
      chip('Cities I serve'),
      chip('Counties'),
      chip('Storefront address'),
      chip('Business hours'),
      chip('Travel radius'),
    ],
  },
  contact: {
    icon: PhoneOutlinedIcon,
    headline: 'How should customers reach you?',
    chips: [
      chip('Phone'),
      chip('Email'),
      chip('Address'),
      chip('Facebook'),
      chip('Google Business'),
    ],
  },
  brand: {
    icon: WaterDropOutlinedIcon,
    headline: 'Make it look like you',
    chips: [],
  },
  competitors: {
    icon: FlagOutlinedIcon,
    headline: 'Anyone you measure up against?',
    chips: [chip('A competitor'), chip('What we do better')],
  },
  favoriteSites: {
    icon: FavoriteBorderOutlinedIcon,
    headline: 'Any sites you like?',
    chips: [chip('A site I like'), chip('What I like about it')],
  },
  proof: {
    icon: ShieldOutlinedIcon,
    headline: 'Show off what makes you trusted',
    chips: [],
  },
  goals: {
    icon: TrackChangesOutlinedIcon,
    headline: 'What should visitors do?',
    chips: [],
  },
  extras: {
    icon: ChatBubbleOutlineOutlinedIcon,
    headline: 'Anything else we should know?',
    chips: [
      chip('A logo I love'),
      chip('A color I want'),
      chip('Something special about us'),
      chip('Anything to avoid'),
    ],
  },
};

/** Metadata for a step id, with a safe fallback for unknown ids. */
export function stepMeta(id) {
  return STEP_META[id] || { icon: HelpOutlineOutlinedIcon, headline: '', chips: [] };
}
